import { TimelineEvent, TimelineDocument } from '@backend/schemas'
import { Slot } from '@radix-ui/react-slot'
import { format } from 'date-fns'
import { MoveRightIcon } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { usePreviewDocument } from '@/hooks/useDocumentPreview'
import { useIndividuals } from '@/hooks/useIndividuals'
import { cn } from '@/lib/utils'
import { getTimelineEventIcon } from '@/utils/icon.utils'

export type ExtendedTimelineEvent = Omit<TimelineEvent, 'documents'> & {
  documents?: TimelineDocument[]
}

type Props = {
  events: ExtendedTimelineEvent[]
  compact?: boolean
}

const getEventDate = (event: ExtendedTimelineEvent): Date => {
  if (event.date) return new Date(event.date)

  const date = new Date(event.year, 0, 1)
  if (event.month) date.setMonth(event.month - 1)
  if (event.day) date.setDate(event.day)

  return date
}

const TimelineVertical = ({ events, compact }: Props) => {
  const { selectedIndividual } = useIndividuals()
  const { t } = useTranslation()

  const birthDate = selectedIndividual?.birthDate
    ? new Date(selectedIndividual.birthDate)
    : new Date()

  const sortedEntries = useMemo(() => {
    return events.sort((a, b) => getEventDate(b).getTime() - getEventDate(a).getTime())
  }, [events])

  const sectionsByAge = useMemo(() => {
    const sections: Record<string, ExtendedTimelineEvent[]> = {}

    sortedEntries.forEach(event => {
      const eventDate = getEventDate(event)
      const age = Math.floor(
        (eventDate.getTime() - birthDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000),
      )

      sections[age] ??= []
      sections[age].push(event)
    })

    return sections
  }, [sortedEntries, birthDate])

  if (!selectedIndividual) return null

  return (
    <div>
      <ol className="flex flex-col gap-4">
        {Object.entries(sectionsByAge)
          .sort(([ageA], [ageB]) => Number(ageB) - Number(ageA))
          .map(([age, events], sectionIndex, arr) => {
            const isLastSection = sectionIndex === arr.length - 1
            // Hide the connector only on the timeline's very last entry. Hiding it
            // per-section breaks the line whenever a section has a single event
            // (common for sparse note timelines split across age groups).
            const entries = events.map((event, entryIndex) => (
              <TimelineEntry
                compact={compact}
                event={event}
                isLastEntry={isLastSection && entryIndex === events.length - 1}
                key={entryIndex}
              />
            ))

            return (
              <div className="flex flex-col" key={age}>
                <ul className={cn('flex flex-col', compact ? 'gap-2' : 'gap-5')}>{entries}</ul>
                {sectionIndex !== arr.length - 1 && (
                  <div className="flex items-center my-8">
                    <Badge size="xl">
                      {t('timeline.birthDateEvent', {
                        age,
                        name: selectedIndividual.firstName,
                      })}
                    </Badge>
                  </div>
                )}
              </div>
            )
          })}
      </ol>
    </div>
  )
}

export const TimelineEntry = ({
  event,
  isLastEntry,
  compact,
}: {
  event: ExtendedTimelineEvent
  isLastEntry?: boolean
  compact?: boolean
}) => {
  const previewDocument = usePreviewDocument()

  const dateText = useMemo(() => {
    if (event.date && event.endDate) {
      return (
        <>
          {event.date}
          <MoveRightIcon className="inline-block mx-2 text-text-secondary" size={14} />
          {event.endDate}
        </>
      )
    }

    if (event.year && event.month && event.day) {
      const date = new Date(event.year, event.month - 1, event.day)

      return format(date, 'yyyy - MMMM d')
    }

    if (event.year && event.month) {
      const date = new Date(event.year, event.month - 1, 1)

      return format(date, 'yyyy - MMMM')
    }

    return event.year.toString()
  }, [event])

  const IconComponent = getTimelineEventIcon(event.type)

  return (
    <li className="flex relative gap-5">
      <div className="flex flex-col gap-5">
        <div className="flex shrink-0 flex-col items-center size-11 rounded-full bg-layer-2 justify-center">
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <Slot className="text-text-primary" {...({ size: 22 } as any)}>
            <IconComponent />
          </Slot>
        </div>
        {/* min-h keeps the connector visible for short entries (e.g. notes,
            which have no document badge so h-full resolves to ~0). */}
        <div
          className={cn(
            'ml-[22px] -translate-x-1/2 w-[1px] bg-text-tertiary h-full rounded-full',
            compact ? 'min-h-4' : 'min-h-12',
            { hidden: isLastEntry },
          )}
        />
      </div>
      <div
        className={cn('py-0.5 w-full min-w-0 flex flex-col', compact ? 'pb-1 gap-1' : 'pb-3 gap-4')}
      >
        <div className="flex flex-col gap-3">
          {/* <p className="text-lg font-semibold text-brand-fg-strong">{event.description}</p> */}
          {event.description && (
            <p className="font-medium text-base max-w-prose">{event.description}</p>
          )}
          <time className="text-sm font-medium leading-none text-text-secondary capitalize">
            {dateText}
          </time>
        </div>
        {event.documents && (
          <div className="flex gap-2 flex-wrap">
            {event.documents.map(doc => (
              <div className="min-w-0 max-w-full" key={doc.id}>
                <Button
                  className="max-w-full"
                  onClick={() => previewDocument(doc.filePath, doc.mimeType)}
                  size="sm"
                  title={doc.name}
                  variant="secondary"
                >
                  <MiddleTruncate text={doc.name} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </li>
  )
}

// Truncates in the middle so the end of the text (e.g. a file extension) stays
// visible: the head shrinks with an ellipsis, the tail never shrinks.
const MiddleTruncate = ({ tailLength = 5, text }: { tailLength?: number; text: string }) => {
  if (text.length <= tailLength * 2) return <span className="truncate">{text}</span>

  return (
    <span className="flex min-w-0">
      <span className="truncate">{text.slice(0, -tailLength)}</span>
      <span className="shrink-0 whitespace-pre">{text.slice(-tailLength)}</span>
    </span>
  )
}

export default TimelineVertical
